import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Dialog from 'components/Dialog';

import {
  dashboardCreationSteps,
  dashboardUpdateSteps,
  widgetCreationSteps
} from './components/Stepper/helpers/handle-steps';
import WidgetPreview from './components/Stepper/helpers/components/WidgetPreview';
import DeletePopup from './components/Stepper/helpers/components/DeletePopup';
import {
  DashboardConnector,
  DashboardCreateStepIcon,
  DashboardUpdateStepIcon,
  WidgetConnector,
  WidgetStepIcon
} from './components/Stepper/helpers/components/icons';
import Stepper from './components/Stepper';
import DashboardsShareForm from './components/DashboardsShareForm';

const createLabels = ['Layout', 'Information', 'Default Configuration', 'Date Settings'];
const createTitles = ['Create New Dashboard', 'Create New Dashboard', 'Create New Dashboard'];

const updateLabels = ['Information', 'Default Configuration', 'Date Settings'];
const updateTitles = ['Update Dashboard', 'Update Dashboard', 'Update Dashboard'];

const updateCannedLabels = ['Information', 'Date Settings'];
const updateCannedTitles = ['Update Dashboard', 'Update Dashboard'];

const widgetLabels = ['Data Configuration', 'Visual Preferences', 'Date Settings'];
const widgetTitles = ['Add New Widget', 'Add New Widget', 'Add New Widget'];

export const POPUPS_MAP = {
  DASHBOARD_EDIT: 'dashboard_edit',
  DASHBOARD_CREATE: 'dashboard_create',
  DASHBOARD_DELETE: 'dashboard_delete',
  DASHBOARD_SHARE: 'dashboard_share',
  WIDGET_CREATE: 'widget_create',
  WIDGET_PREVIEW: 'widget_preview'
};

const Popups = ({
  which,
  dashboardData,
  dispatchDashboard,
  baseWidgetWidth,
  setBaseWidgetWidth,
  baseWidgetId,
  setWhichPopup,
  handleBaseWidgetClick,
  setBaseWidgetPlaceholder,
  panelIndex
}) => {
  const history = useHistory();

  const hasUserDraggedRootScrollbar = ev => {
    const { clientHeight, clientWidth } = ev.currentTarget;
    let result = false;

    if (clientWidth < ev.clientX || clientHeight < ev.clientY) {
      // This is a temporary workaround for when a user decides to
      // scroll the content of an opened dialog component by dragging the root scrollbar.
      // This should be fixed in Material-UI V5.
      // https://github.com/mui-org/material-ui/issues/21694
      // https://github.com/mui-org/material-ui/issues/23887
      result = true;
    }

    return result;
  };

  const handleWidgetClose = ev => {
    if (ev && hasUserDraggedRootScrollbar(ev)) return;

    setBaseWidgetPlaceholder();
    setWhichPopup();
  };

  const handleDashboardCreationClose = ev => {
    if (hasUserDraggedRootScrollbar(ev)) return;

    setWhichPopup();
    history.goBack();
  };

  const handleDashboardEditClose = ev => {
    if (hasUserDraggedRootScrollbar(ev)) return;
    setWhichPopup();
  };

  switch (which) {
    case POPUPS_MAP.DASHBOARD_SHARE:
      return (
        <Dialog
          isOpen={which === POPUPS_MAP.DASHBOARD_SHARE}
          onClose={() => setWhichPopup()}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="sm"
        >
          <DashboardsShareForm dashboardId={dashboardData.id} onClose={() => setWhichPopup()} />
        </Dialog>
      );
    case POPUPS_MAP.DASHBOARD_CREATE:
      return (
        <Dialog
          isOpen={which === POPUPS_MAP.DASHBOARD_CREATE}
          onClose={handleDashboardCreationClose}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="sm"
        >
          <Stepper
            setWhichPopup={setWhichPopup}
            dashboardData={dashboardData}
            dispatchDashboard={dispatchDashboard}
            handleSteps={dashboardCreationSteps}
            labels={createLabels}
            titles={createTitles}
            connector={DashboardConnector}
            iconComponent={DashboardCreateStepIcon}
          />
        </Dialog>
      );
    case POPUPS_MAP.DASHBOARD_EDIT:
      return (
        <Dialog
          isOpen={which === POPUPS_MAP.DASHBOARD_EDIT}
          onClose={handleDashboardEditClose}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="sm"
        >
          <Stepper
            isUpdate
            setWhichPopup={setWhichPopup}
            dashboardData={dashboardData}
            dispatchDashboard={dispatchDashboard}
            handleSteps={dashboardUpdateSteps}
            labels={dashboardData.isCanned ? updateCannedLabels : updateLabels}
            titles={dashboardData.isCanned ? updateCannedTitles : updateTitles}
            connector={DashboardConnector}
            iconComponent={DashboardUpdateStepIcon}
          />
        </Dialog>
      );

    case POPUPS_MAP.WIDGET_PREVIEW:
      return (
        <Dialog
          isOpen={which === POPUPS_MAP.WIDGET_PREVIEW}
          onClose={() => setWhichPopup()}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="md"
        >
          <WidgetPreview handleBaseWidgetClick={handleBaseWidgetClick} />
        </Dialog>
      );

    case POPUPS_MAP.DASHBOARD_DELETE:
      return (
        <Dialog
          isOpen={which === POPUPS_MAP.DASHBOARD_DELETE}
          onClose={() => setWhichPopup()}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="sm"
        >
          <DeletePopup onClose={() => setWhichPopup()} />
        </Dialog>
      );

    case POPUPS_MAP.WIDGET_CREATE:
      return (
        <Dialog
          isOpen={baseWidgetId && which === POPUPS_MAP.WIDGET_CREATE}
          onClose={handleWidgetClose}
          hasBorder
          backdrop="#346C9F73"
          maxWidth="sm"
        >
          <Stepper
            labels={widgetLabels}
            titles={widgetTitles}
            setWhichPopup={setWhichPopup}
            dashboardData={dashboardData}
            dispatchDashboard={dispatchDashboard}
            handleSteps={widgetCreationSteps}
            baseWidgetId={baseWidgetId}
            baseWidgetWidth={baseWidgetWidth}
            setBaseWidgetWidth={setBaseWidgetWidth}
            connector={WidgetConnector}
            iconComponent={WidgetStepIcon}
            handleWidgetClose={handleWidgetClose}
            panelIndex={panelIndex}
          />
        </Dialog>
      );

    default:
      return null;
  }
};

Popups.propTypes = {
  which: PropTypes.string,
  baseWidgetId: PropTypes.string,
  dashboardData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isCanned: PropTypes.bool.isRequired
  }),
  dispatchDashboard: PropTypes.func,
  baseWidgetWidth: PropTypes.number,
  setBaseWidgetWidth: PropTypes.func,
  setWhichPopup: PropTypes.func,
  handleBaseWidgetClick: PropTypes.func,
  setBaseWidgetPlaceholder: PropTypes.func,
  panelIndex: PropTypes.number
};

Popups.defaultProps = {
  which: null,
  dashboardData: {},
  dispatchDashboard: null,
  baseWidgetId: null,
  baseWidgetWidth: null,
  setWhichPopup: null,
  setBaseWidgetWidth: null,
  handleBaseWidgetClick: null,
  setBaseWidgetPlaceholder: null,
  panelIndex: null
};

export default Popups;
